/**
 * useTVRemoteControl Hook
 *
 * Manages TV remote control functionality including:
 * - Keyboard event handling
 * - Arrow navigation
 * - Focus management
 * - TV-specific styling
 * - Remote button mapping
 *
 * TODO: Extract keyboard event listeners from gallery.js
 * TODO: Move arrow navigation handling to hook
 * TODO: Extract focus management functionality
 * TODO: Add TV device detection logic
 * TODO: Create remote button mapping utilities
 */

import { useEffect, useCallback } from 'react'
import { useLGDetection } from '../../LG/hooks/useLGDetection'

/**
 * Custom hook for handling TV remote control functionality
 * Extracts TV remote logic from Gallery, InfiniteScrollContainer, and Modal components
 */
export const useTVRemoteControl = ({
  // Gallery navigation handlers
  onArrowLeft,
  onArrowRight,
  onArrowUp,
  onArrowDown,
  onBrowserBack,

  // Modal navigation handlers
  onModalArrowLeft,
  onModalArrowRight,
  isModalActive = false,

  // Asset scroll handlers
  onScrollUp,
  onScrollDown,
  onLoadMore,

  // Refs for arrow elements
  leftArrowRefs,
  rightArrowRefs,

  // Configuration
  enableGalleryNavigation = true,
  enableModalNavigation = true,
  enableAssetScrolling = true,

  // Device detection
  loadTVStyles = true
}) => {
  // LG TV detection
  const { isLGSmartTV } = useLGDetection()

  // Device detection
  const isTVDevice = isLGSmartTV

  // Load TV-specific styles
  useEffect(() => {
    if (loadTVStyles && isTVDevice) {
      require('../../../assets/stylesheets/gallery-tv-style.css')
    } else if (loadTVStyles) {
      require('../../../assets/stylesheets/gallery-style.css')
    }
  }, [loadTVStyles, isTVDevice])

  // Gallery navigation keyboard handler
  const handleGalleryKeyPress = useCallback(
    e => {
      if (!enableGalleryNavigation) return

      switch (e.code) {
        case 'ArrowLeft':
          if (leftArrowRefs?.current?.[0]) {
            leftArrowRefs.current[0].click()
          } else if (onArrowLeft) {
            onArrowLeft(e)
          }
          break
        case 'ArrowRight':
          if (rightArrowRefs?.current?.[0]) {
            rightArrowRefs.current[0].click()
          } else if (onArrowRight) {
            onArrowRight(e)
          }
          break
        case 'ArrowUp':
          if (onArrowUp) {
            onArrowUp(e)
          }
          break
        case 'ArrowDown':
          if (onArrowDown) {
            onArrowDown(e)
          }
          break
        case 'BrowserBack':
          if (onBrowserBack) {
            onBrowserBack(e)
          }
          break
        default:
          break
      }
    },
    [
      enableGalleryNavigation,
      leftArrowRefs,
      rightArrowRefs,
      onArrowLeft,
      onArrowRight,
      onArrowUp,
      onArrowDown,
      onBrowserBack
    ]
  )

  // Modal navigation keyboard handler
  const handleModalKeyPress = useCallback(
    e => {
      if (!enableModalNavigation || !isModalActive) return

      switch (e.code) {
        case 'ArrowLeft':
          if (onModalArrowLeft) {
            onModalArrowLeft(e)
          }
          break
        case 'ArrowRight':
          if (onModalArrowRight) {
            onModalArrowRight(e)
          }
          break
        default:
          break
      }
    },
    [enableModalNavigation, isModalActive, onModalArrowLeft, onModalArrowRight]
  )

  // Asset scrolling keyboard handler
  const handleAssetScrollKeyPress = useCallback(
    e => {
      if (!enableAssetScrolling) return

      switch (e.code) {
        case 'ArrowUp':
          if (onScrollUp) {
            onScrollUp(e)
          }
          break
        case 'ArrowDown':
          if (onScrollDown) {
            onScrollDown(e)
          }
          if (onLoadMore) {
            onLoadMore(e)
          }
          break
        default:
          break
      }
    },
    [enableAssetScrolling, onScrollUp, onScrollDown, onLoadMore]
  )

  // Combined keyboard handler
  const handleKeyPress = useCallback(
    e => {
      // Modal navigation takes priority when modal is active
      if (isModalActive && enableModalNavigation) {
        handleModalKeyPress(e)
        return
      }

      // Gallery navigation
      if (enableGalleryNavigation) {
        handleGalleryKeyPress(e)
      }

      // Asset scrolling (can work alongside gallery navigation)
      if (enableAssetScrolling) {
        handleAssetScrollKeyPress(e)
      }
    },
    [
      isModalActive,
      enableModalNavigation,
      enableGalleryNavigation,
      enableAssetScrolling,
      handleModalKeyPress,
      handleGalleryKeyPress,
      handleAssetScrollKeyPress
    ]
  )

  // Set up keyboard event listeners
  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [handleKeyPress])

  // Reset arrow refs helper (for Gallery component)
  const resetArrowRefs = useCallback(() => {
    if (leftArrowRefs?.current) {
      leftArrowRefs.current = []
    }
    if (rightArrowRefs?.current) {
      rightArrowRefs.current = []
    }
  }, [leftArrowRefs, rightArrowRefs])

  // Focus management for TV devices
  const focusElement = useCallback(
    element => {
      if (isTVDevice && element) {
        element.focus()
      }
    },
    [isTVDevice]
  )

  return {
    isTVDevice,
    resetArrowRefs,
    focusElement,

    // Navigation handlers
    handleGalleryKeyPress,
    handleModalKeyPress,
    handleAssetScrollKeyPress
  }
}

export default useTVRemoteControl
